import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { FloatingSettings } from '../../components/landings/FloatingSettings';
import { BackgroundSettings } from '../../components/landings/BackgroundSettings';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { ThankYouView } from '../../components/public/ThankYouView';
import { DEFAULT_REPLY_NOTE, DEFAULT_WHATSAPP_MESSAGE } from '../../data/thankYou';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
import { normalizeSlug, validateSlug } from '../../config/app';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  /** Solo se arrastra desde el asa, para no mover bloques por accidente. */
  const [handleId, setHandleId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  /**
   * Estado de publicación, distinto del de guardado:
   * - 'limpio': nada pendiente.
   * - 'pendiente': hay cambios guardados que aún no se han publicado.
   * - 'publicado': la última acción fue publicar.
   */
  const [publishState, setPublishState] = useState<'limpio' | 'pendiente' | 'publicado'>('limpio');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  /** Pestaña activa: con «gracias» la vista previa muestra la página de gracias. */
  const [tab, setTab] = useState('bloques');
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-1352" data-mp="3jmcoobyr6ns">
        <p className="text-sm text-muted-foreground" data-id="element-1353" data-mp="3m0ghyng43jkj">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-1354" data-mp="3h2eyi1erxi8j">
          <Button variant="secondary" data-id="element-1355" data-mp="3h02me4vp623e">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  /*
    La vista previa debe resolver el logotipo con la MISMA cadena que la landing
    pública (`components/public/LandingView.tsx`): logo propio de la landing →
    logo de la empresa → identificador de Sistema AVA. Antes el constructor
    pasaba `draft` sin resolver, así que mostraba el identificador genérico
    aunque la empresa tuviera logotipo, y la página pública mostraba otro.
  */
  const previewLanding: LandingPage = {
    ...draft,
    pageConfig: {
      ...draft.pageConfig,
      logoUrl: draft.pageConfig.logoUrl || organization?.brand.logoUrl || organization?.logoUrl
    }
  };
  // Disponibilidad del enlace público: se comprueba contra el resto de landings.
  const slugCheck = validateSlug(draft.slug, landings.filter(l => l.id !== draft.id).map(l => l.slug));
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
    setPublishState('pendiente');
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  /**
   * Reordena solo en vertical: se mueve el objeto completo del bloque, así que
   * conserva id, type, config, style, contenido y visibilidad.
   */
  function reorder(targetId: string) {
    setDragOverId(null);
    setHandleId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    // El enlace público debe ser válido y único antes de guardar o publicar.
    const check = validateSlug(draft!.slug, landings.filter(l => l.id !== draft!.id).map(l => l.slug));
    if (!check.valid) {
      setSaveError(check.reason ?? 'El enlace no es válido.');
      toast.error('Revisa el enlace público', {
        description: check.reason
      });
      return;
    }
    setSaving(true);
    setSaveError(null);
    // Instrumentación: del clic hasta que la interfaz vuelve a estar lista.
    const clicked = performance.now();
    try {
      /*
        Se guarda el logotipo ya resuelto. La tabla `organizations` no es
        legible por un visitante anónimo, así que la landing pública no puede
        recurrir al logo de la empresa: si la landing no guarda su propia URL,
        el móvil se queda sin logotipo aunque el constructor lo muestre.
      */
      await saveLanding(previewLanding);
      if (status) {
        await setLandingStatus(draft!.id, status);
        update({
          status
        });
      }
      setSaved(true);
      // Guardar nunca cambia de página ni recarga: solo confirma.
      if (status === 'publicada') {
        setPublishState('publicado');
        toast.success('Landing publicada correctamente', {
          description: landingUrl(draft!.slug)
        });
      } else if (status === 'despublicada') {
        setPublishState('pendiente');
        toast.success('Landing despublicada', {
          description: 'Su enlace público deja de estar disponible.'
        });
      } else {
        // Guardar conserva el estado actual: si estaba publicada, sigue
        // publicada y la URL pública muestra ya los cambios.
        setPublishState(draft!.status === 'publicada' ? 'publicado' : 'pendiente');
        toast.success(draft!.status === 'publicada' ? 'Cambios guardados y publicados' : 'Cambios guardados');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido.';
      setSaveError(message);
      toast.error('No se ha podido guardar', {
        description: message
      });
    } finally {
      setSaving(false);
      console.log(`[${status === 'publicada' ? 'AVA PUBLISH' : 'AVA SAVE'}] clic → interfaz lista: ${(performance.now() - clicked).toFixed(0)} ms`);
    }
  }
  /** Copia la URL pública, con confirmación visible. */
  async function handleCopyLink() {
    const ok = await copyToClipboard(landingUrl(draft!.slug));
    if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace', {
      description: landingUrl(draft!.slug)
    });
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1356" data-mp="1vnquqgk0fv1q">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1357" data-mp="ua2pb6hbkwaj" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1358" data-mp="z4zwe7v1gvks">
        <div data-id="element-1359" data-mp="kbwqwyuq28af">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-1360" data-mp="p8wv1480h0yw">
            {draft.name}
          </h1>
          <p className="break-all text-sm text-muted-foreground" data-id="element-1361" data-mp="1cxxzkgdq3ryw">
            {landingUrl(draft.slug)} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1362" data-mp="136lhuhln2i6l">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-1363" data-mp="186pnfcl0dsi1">
              Guardando…
            </span> : !saved ? <span className="self-center text-sm text-amber-600" data-id="element-1364" data-mp="1vwk11wd5jl3c">
              Cambios sin guardar
            </span> : publishState === 'publicado' ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1365" data-mp="3leh6enwge1pz">
              <CheckIcon className="h-4 w-4" data-id="element-1366" data-mp="3lc4g9ngjkl1u" /> Publicado correctamente
            </span> : <span className="self-center text-sm text-amber-600" data-id="element-1367" data-mp="3gf49tbfs5ga7">
              Borrador guardado · pendiente de publicar
            </span>}
          {/*
            Guardar NO cambia el estado de publicación. Antes este botón
            llamaba a handleSave('borrador'), que devolvía a borrador una
            landing ya publicada: su URL pública pasaba a responder «esta
            página no existe» en cuanto se guardaba un cambio.
           */}
          <Button variant="ghost" onClick={() => handleSave()} loading={saving} data-id="element-1368" data-mp="3bgt1j554vdu3">
            Guardar cambios
          </Button>
          {draft.status === 'publicada' && <Button variant="ghost" onClick={() => handleSave('despublicada')} loading={saving} data-id="element-1369" data-mp="37h2cp6z637s7">
              Despublicar
            </Button>}
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-1371" data-mp="mo79xi1kmq5d" />} onClick={() => window.open(`/${draft.slug}`, '_blank')} data-id="element-1370" data-mp="mp8jfi2xv4kz">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-1373" data-mp="3kgqsqssxzq3t" />} onClick={handleCopyLink} data-id="element-1372" data-mp="3khs6x6598spr">
            Copiar enlace
          </Button>
          <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-1374" data-mp="3phwnsxa2rpgi">
            {draft.status === 'publicada' ? 'Actualizar publicación' : 'Publicar'}
          </Button>
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-1375" data-mp="2wqd2n7tx41bv">
          <span data-id="element-1376" data-mp="35rupd91tz19j">
            <strong className="font-semibold" data-id="element-1377" data-mp="35rb3jh927sq8">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-1378" data-mp="2e1ycsnvc6tcn">
            Reintentar
          </Button>
        </div>}

      <div className="grid items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-1379" data-mp="2i28qkmpdeo22">
        {/* Panel izquierdo, con scroll propio para no perder la vista previa */}
        <div className="min-w-0 xl:max-h-[860px] xl:overflow-y-auto xl:pr-1" data-id="element-1380" data-mp="25l7fx8ubxqw5">
          <Tabs defaultValue="bloques" value={tab} onValueChange={setTab} data-id="element-1381" data-mp="25j4s8vgng9ul">
            <TabsList data-id="element-1382" data-mp="21hj6ekm50l89">
              <TabsTrigger value="bloques" data-id="element-1383" data-mp="21l6x7c076tja">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-1384" data-mp="1wjre3phkyx3j">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-1385" data-mp="1rlpyjejd5fwt">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-1386" data-mp="1mupyi0ey6hq3">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-1387" data-mp="1mtoev7jv8mwr">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-1388" data-mp="1drotk31tiwo0">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable={handleId === block.id} onDragStart={e => {
                  setDragId(block.id);
                  e.dataTransfer.effectAllowed = 'move';
                }} onDragEnd={() => {
                  setDragId(null);
                  setDragOverId(null);
                  setHandleId(null);
                }} onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragId && dragId !== block.id) setDragOverId(block.id);
                }} onDragLeave={() => setDragOverId(prev => prev === block.id ? null : prev)} onDrop={() => reorder(block.id)} className={`relative rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-50' : ''} ${dragOverId === block.id ? 'before:absolute before:-top-1 before:left-0 before:h-0.5 before:w-full before:rounded-full before:bg-primary' : ''}`} data-id="element-1389" data-mp="13xy9rbgmzaen">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-1390" data-mp="eetbl0hfz6h1">
                        <span onPointerDown={() => setHandleId(block.id)} onPointerUp={() => setHandleId(null)} className="flex h-8 w-6 flex-none cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-1391" data-mp="1yzgcb54s94v9">
                          <GripVerticalIcon className="h-4 w-4" data-id="element-1392" data-mp="23yb3ovgtg1qz" />
                        </span>
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-1393" data-mp="2hp65xowt7kll">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1394" data-mp="2cpjvfn8d8igh">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-1395" data-mp="2mo9dbg09i2wq">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1396" data-mp="sbjgapelkdl4">
                          <BookmarkIcon className="h-4 w-4" data-id="element-1397" data-mp="1qyaav8300qux" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1398" data-mp="1vzg6bdo1ms85">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-1399" data-mp="29qctuq344j8c" /> : <EyeOffIcon className="h-4 w-4" data-id="element-1400" data-mp="2eowjp1ktuf5z" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1401" data-mp="2eqqtpkuk8mva">
                          <CopyIcon className="h-4 w-4" data-id="element-1402" data-mp="2onc3ejhiym5g" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-1403" data-mp="cxdyrbqehyi4">
                            <Trash2Icon className="h-4 w-4" data-id="element-1404" data-mp="3u9yj4p5izt4b" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-1405" data-mp="qrf26isas9ot">
                          <Tabs defaultValue="contenido" data-id="element-1406" data-mp="qt86hrffuhtn">
                            <TabsList data-id="element-1407" data-mp="lzdhc8h2dan6">
                              <TabsTrigger value="contenido" data-id="element-1408" data-mp="lyc6o53ymq5z">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-1409" data-mp="h125xtb4es2u">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-1410" data-mp="1eikewp2xyluw">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-1411" data-mp="19p7rujdwun49" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-1412" data-mp="i14yyanxvc4p">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-1413" data-mp="mtelrhtrbw2r" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1415" data-mp="11rgsg0w34xo8" />} onClick={() => setAddOpen(true)} data-id="element-1414" data-mp="dyqj7zfkgshn">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-1416" data-mp="rs81lgwinz63">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1417" data-mp="rr6hkmxiw2c8">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-1418" data-mp="wq9lg41ro2im" />
                <div data-id="element-1419" data-mp="3hhjsw1584x6j">
                  <Input id="l-slug" label="Slug (enlace público)" helperText={landingUrl(draft.slug)} value={draft.slug} error={slugCheck.valid ? undefined : slugCheck.reason} onChange={e => update({
                  slug: normalizeSlug(e.target.value)
                })} data-id="element-1420" data-mp="3hi32mukq0f9h" />
                  {slugCheck.valid && <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-green-600" data-id="element-1421" data-mp="3iz8ootiw7c1n">
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1422" data-mp="3dzdzcuermuip" />
                      Enlace disponible
                    </p>}
                </div>
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-1423" data-mp="2q7p0ev7fv7ss" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-1424" data-mp="27jacwhwbybwq" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-1425" data-mp="yzrirh3h3wsd" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-1426" data-mp="18qdqyq8v1l11" />
                <div data-id="element-1427" data-mp="1hq0qt7lxyirz">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1428" data-mp="1hp7hpzzifh6o">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-1429" data-mp="1royd03vwa07t" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-1430" data-mp="1mr6kb7nfea2k">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Elementos flotantes opcionales de esta landing */}
              <BackgroundSettings value={draft.pageConfig.background} onChange={background => updateConfig({
              background
            })} data-id="element-1431" data-mp="23yxrractu1rk" />
              <FloatingSettings value={draft.pageConfig.floating} onChange={floating => updateConfig({
              floating
            })} data-id="element-1432" data-mp="290dcf1b3e69f" />

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-1433" data-mp="1z6efqhtsfeov">
                <div className="mb-3 flex items-center gap-2" data-id="element-1434" data-mp="1l7er3a69yzsp">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1435" data-mp="1q82rgmge8szl" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-1436" data-mp="1bb1yygqdou02">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-1437" data-mp="35ot4p55l6tq8">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-1438" data-mp="396h3jjp16q89">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-1439" data-mp="394e82k5404w5">
                        <span className="min-w-0 flex-1" data-id="element-1440" data-mp="2z8b7hipwoebb">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1441" data-mp="2z8t9kvz91mr5">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-1442" data-mp="3rx8f8rwhqspt">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-1443" data-mp="3i6dpveny7d05">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-1444" data-mp="eilsn5p7csim">
                          <Trash2Icon className="h-4 w-4" data-id="element-1445" data-mp="1sedfbrixucwu" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-1446" data-mp="21es1et3q7w1e">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1447" data-mp="21dqr4qujbdkq">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-1448" data-mp="26exv0etk3sm4" />
                <Input id="t-subtitle" label="Subtítulo" value={draft.pageConfig.thankYou.subtitle ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  subtitle: e.target.value
                }
              })} data-id="element-1449" data-mp="2l30ztkvv4y9c" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-1450" data-mp="2z2aj4tfd88hj" />
                <Switch id="t-video" label="Mostrar vídeo" checked={Boolean(draft.pageConfig.thankYou.showVideo)} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showVideo: checked
                }
              })} data-id="element-1451" data-mp="2gew3sm7lac0o" />
                <Input id="t-videourl" label="URL del vídeo (YouTube, Vimeo, Wistia o mp4)" value={draft.pageConfig.thankYou.videoUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoUrl: e.target.value
                }
              })} data-id="element-1452" data-mp="22isdh3zu6nth" />
                <Input id="t-videothumb" label="Miniatura del vídeo (URL)" value={draft.pageConfig.thankYou.videoThumbnailUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoThumbnailUrl: e.target.value
                }
              })} data-id="element-1453" data-mp="3ntsgqi45d245" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-1454" data-mp="3vemf7lg77rjo" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-1455" data-mp="d150g6p1yqfp" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-1456" data-mp="87k7it2bgmnd" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-1457" data-mp="1akloawdpe8x0" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-1458" data-mp="wnq2028fykz0" />
                {draft.pageConfig.thankYou.showWhatsapp && <>
                    <Input id="t-waeyebrow" label="Texto pequeño superior" placeholder="Solo falta un paso" value={draft.pageConfig.thankYou.whatsappEyebrow ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappEyebrow: e.target.value
                  }
                })} data-id="element-1459" data-mp="dwfgy1ped2f1" />
                    <Input id="t-watitle" label="Título del paso de WhatsApp" placeholder="Abre WhatsApp" value={draft.pageConfig.thankYou.whatsappStepTitle ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappStepTitle: e.target.value
                  }
                })} data-id="element-1460" data-mp="3uwyzx934rmdk" />
                    <Input id="t-watext" label="Texto del paso de WhatsApp" placeholder="Tu solicitud ya está preparada." value={draft.pageConfig.thankYou.whatsappStepText ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappStepText: e.target.value
                  }
                })} data-id="element-1461" data-mp="3c6r3taxulw6v" />
                    <Input id="t-walabel" label="Texto del botón" placeholder="Continuar por WhatsApp" value={draft.pageConfig.thankYou.whatsappLabel ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappLabel: e.target.value
                  }
                })} data-id="element-1462" data-mp="2oflm0q8ckttx" />
                    <p className="text-xs leading-relaxed text-muted-foreground" data-id="element-1463" data-mp="lgklfqpqxyno">
                      Variables disponibles: {'{{nombre}} {{empresa}} {{telefono}} {{correo}} {{ciudad}} {{instagram}} {{descripcion}} {{servicio}} {{publicidad}} {{presupuesto}} {{comentarios}}'}. Las líneas cuyas variables queden vacías se eliminan solas.
                    </p>
                    <Textarea id="t-wamsg" rows={10} label="Mensaje automático" value={draft.pageConfig.thankYou.whatsappMessage ?? DEFAULT_WHATSAPP_MESSAGE} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappMessage: e.target.value
                  }
                })} data-id="element-1464" data-mp="146sh5nr49mtu" />
                    <Switch id="t-wapreview" label="Mostrar vista previa del mensaje" checked={draft.pageConfig.thankYou.showWhatsappPreview !== false} onChange={checked => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    showWhatsappPreview: checked
                  }
                })} data-id="element-1465" data-mp="19396gla1y78i" />
                  </>}
                <Switch id="t-replynote" label="Mostrar el plazo de respuesta" checked={draft.pageConfig.thankYou.showReplyNote !== false} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showReplyNote: checked
                }
              })} data-id="element-1466" data-mp="1nqhlxyae8yqa" />
                {draft.pageConfig.thankYou.showReplyNote !== false && <Input id="t-replytext" label="Plazo de respuesta" placeholder={DEFAULT_REPLY_NOTE} value={draft.pageConfig.thankYou.replyNote ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  replyNote: e.target.value
                }
              })} data-id="element-1467" data-mp="152cmh30rrzxa" />}
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-1468" data-mp="r1aouzn2zwkx" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-1469" data-mp="19xtql3vk2i7g" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable, fija mientras el panel izquierdo se desplaza */}
        <div className="h-[860px] min-w-0 xl:sticky xl:top-4" data-id="element-1470" data-mp="1sm8idtdp609b">
          <DevicePreview device={device} onDeviceChange={setDevice} onBlockSelect={setSelectedBlockId} isolate toolbar={tab === 'gracias' ? <span className="text-xs text-muted-foreground" data-id="element-1472" data-mp="3i3cdvb2qtb9l">
                  Vista previa de la página de gracias
                </span> : selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-1473" data-mp="3948us73ksvv3">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-1474" data-mp="347ifhlzli4aq">
                  Haz clic en una sección para editarla
                </span>} data-id="element-1471" data-mp="1xo67h48s3pa6">
            {tab === 'gracias' ? <ThankYouView config={draft.pageConfig.thankYou} logoUrl={draft.pageConfig.logoUrl ?? organization?.brand.logoUrl ?? organization?.logoUrl} primaryColor={draft.pageConfig.primaryColor} fallbackPhone={draft.pageConfig.floating?.whatsapp?.phone} preview data-id="element-1475" data-mp="op51i245vks" /> : <LandingRenderer landing={previewLanding} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} viewport={device === 'mobile' || device === 'iphone16' ? 'mobile' : device === 'tablet' ? 'tablet' : 'desktop'} brandName={organization?.name} data-id="element-1476" data-mp="3n2pagnjf1wpd" />}
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-1477" data-mp="17jejtukpakde">
        {components.length > 0 && <div className="mb-6" data-id="element-1478" data-mp="tqosdamekn8w">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1479" data-mp="tpnktg4liivx">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1480" data-mp="jtaqqh55230n">
              {components.map(component => <li key={component.id} data-id="element-1481" data-mp="osdiizw3ycqo">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1482" data-mp="ou884ribqt13">
                    <span className="block text-sm font-medium text-foreground" data-id="element-1483" data-mp="ewk0x23j7t21">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-1484" data-mp="298s1ow2jb45q">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1485" data-mp="3pq8vdquxv7cf">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1486" data-mp="7cszkofec2av">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-1487" data-mp="2i56sddd0lqh">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1488" data-mp="h8micpjpks13">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-1489" data-mp="qa2js7sjpjt6">
                  {definition.label}
                  {definition.category && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary" data-id="element-1490" data-mp="l8woq3bexkgh">
                      {definition.category}
                    </span>}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-1491" data-mp="2phaklj5t96l8">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-1493" data-mp="33g0j938a3ebp">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-1494" data-mp="25qxerzgkgn89">Guardar componente</Button>
          </>} data-id="element-1492" data-mp="2ygxjnyb2i0b4">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-1495" data-mp="2aps7bt4c5q5k" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-1496" data-mp="2ki6bstmstavf">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}